import { z } from 'zod'

/**
 * Organization Alert Text: one line an Organization Administrator shows in
 * the header of every clinic of their Organization. onboarding-api owns the
 * record (`organization_alert.py`); these mirror its contract and limits.
 */
export const ALERT_MESSAGE_MAX_LENGTH = 280
export const ALERT_COLOR_PATTERN = /^#[0-9a-f]{6}$/

const color = z.string().regex(ALERT_COLOR_PATTERN)
const message = z.string().max(ALERT_MESSAGE_MAX_LENGTH * 2)

export const administratorAlertSchema = z.object({
  organizationId: z.string().regex(/^org_[0-9a-f]{32}$/),
  alert: z
    .object({
      revision: z.number().int().positive(),
      active: z.boolean(),
      message,
      color,
      updatedAtMs: z.number().int().nonnegative(),
    })
    .nullable(),
})

export type AdministratorAlert = z.infer<typeof administratorAlertSchema>

export const clinicAlertSchema = z.object({
  tenantUid: z.string().min(1).max(128),
  alert: z
    .object({
      revision: z.number().int().positive(),
      message: message.min(1),
      color,
    })
    .nullable(),
})

export type ClinicAlert = z.infer<typeof clinicAlertSchema>

export type AlertSave = {
  expectedRevision: number
  active: boolean
  message: string
  color: string
}

/** The message as the server stores it: one line, single spaces, trimmed. */
export function normalizeAlertMessage(value: string) {
  return value.split(/\s+/u).filter(Boolean).join(' ')
}

/** Length in code points, as the server counts it. */
export function alertMessageLength(value: string) {
  return [...value].length
}

// Control, surrogate and private-use characters, and format characters such
// as bidirectional overrides and zero-width spaces, as the server refuses
// them. The joiners and tags that emoji and some scripts need are kept.
const REFUSED_CATEGORIES = /[\p{Cc}\p{Cs}\p{Co}]/u
const FORMAT = /\p{Cf}/u

// Zero-width non-joiner and joiner, and the emoji tag characters.
function allowedFormat(character: string) {
  const code = character.codePointAt(0) ?? 0
  return (
    code === 0x200c || code === 0x200d || (code >= 0xe0020 && code <= 0xe007f)
  )
}

function refusedCharacter(character: string) {
  return (
    REFUSED_CATEGORIES.test(character) ||
    (FORMAT.test(character) && !allowedFormat(character))
  )
}

export function alertMessageProblem(value: string, active: boolean) {
  const normalized = normalizeAlertMessage(value)
  if (alertMessageLength(normalized) > ALERT_MESSAGE_MAX_LENGTH)
    return `Keep the message to ${ALERT_MESSAGE_MAX_LENGTH} characters.`
  if ([...normalized].some(refusedCharacter))
    return 'Remove hidden or control characters from the message.'
  if (active && !normalized) return 'Enter the message to show.'
  return null
}

export function normalizeAlertColor(value: string) {
  const lower = value.trim().toLowerCase()
  return ALERT_COLOR_PATTERN.test(lower) ? lower : null
}
